"use client";

import { c as _c } from "react/compiler-runtime";
import { formatMemesQuickVoteLeftThisRoundText, formatMemesQuickVoteUnratedText, getDefaultQuickVoteAmount, normalizeQuickVoteAmount } from "@/hooks/memesQuickVote.helpers";
import type { MemesQuickVoteDialogState } from "@/hooks/useMemesQuickVoteDialogController";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { MemesQuickVoteDialogDoneState, MemesQuickVoteDialogErrorState, MemesQuickVoteDialogRestartState } from "./MemesQuickVoteDialogStates";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import MemesQuickVoteControls from "./MemesQuickVoteControls";
import MemesQuickVoteDialogSkeleton from "./MemesQuickVoteDialogSkeleton";
import MemesQuickVotePreview from "./MemesQuickVotePreview";
const QUICK_VOTE_MOBILE_QUERY = "(max-width: 767px)";
const QUICK_VOTE_BAR_FEEDBACK_DURATION_MS = 650;
type VoteFeedbackSource = "custom-submit" | "quick-amount";
type TimeoutHandle = ReturnType<typeof globalThis.setTimeout>;
type MemesQuickVoteDialogProps = MemesQuickVoteDialogState;
interface MemesQuickVoteDialogContentProps {
  readonly activeDrop: NonNullable<MemesQuickVoteDialogProps["activeDrop"]>;
  readonly isMobile: boolean;
  readonly leftThisRoundCount: number;
  readonly latestUsedAmount: number | null;
  readonly nextDrop: MemesQuickVoteDialogProps["nextDrop"];
  readonly onClose: () => void;
  readonly recentAmounts: number[];
  readonly sessionId: number;
  readonly submitVote: MemesQuickVoteDialogProps["submitVote"];
  readonly skipDrop: MemesQuickVoteDialogProps["skipDrop"];
  readonly uncastPower: number | null;
  readonly unratedCount: number;
  readonly votingLabel: string | null;
}
interface MemesQuickVotePreviewPaneProps {
  readonly activeDrop: NonNullable<MemesQuickVoteDialogProps["activeDrop"]>;
  readonly className: string;
  readonly isBusy: boolean;
  readonly isMobile: boolean;
  readonly leftThisRoundCount: number;
  readonly nextDrop: MemesQuickVoteDialogProps["nextDrop"];
  readonly onAdvanceStart: () => void;
  readonly onSkip: () => void;
  readonly onVoteWithSwipe: () => void;
  readonly sessionId: number;
  readonly swipeVoteAmount: number | null;
  readonly uncastPower: number | null;
  readonly unratedCount: number;
  readonly votingLabel: string | null;
}
interface MemesQuickVoteControlsPaneProps {
  readonly className: string;
  readonly customValue: string;
  readonly drop: NonNullable<MemesQuickVoteDialogProps["activeDrop"]>;
  readonly feedbackAmount: number | null;
  readonly feedbackSource: VoteFeedbackSource | null;
  readonly isCustomOpen: boolean;
  readonly isSubmitting: boolean;
  readonly isVoteFeedbackActive: boolean;
  readonly leftThisRoundCount: number;
  readonly latestUsedAmount: number | null;
  readonly quickAmounts: readonly number[];
  readonly uncastPower: number | null;
  readonly unratedCount: number;
  readonly votingLabel: string | null;
  readonly onCustomChange: (value: string) => void;
  readonly onCustomSubmit: () => void;
  readonly onOpenCustom: () => void;
  readonly onSkip: () => void;
  readonly onVoteAmount: (amount: number) => void;
}
function getQuickVotePreloadedNextDrop(isOpen: boolean, nextDrop: MemesQuickVoteDialogProps["nextDrop"]): MemesQuickVoteDialogProps["nextDrop"] {
  if (!isOpen || !nextDrop) {
    return null;
  }
  const artworkMedia = nextDrop.parts.at(0)?.media.at(0);
  const mediaMimeType = artworkMedia?.mime_type.toLowerCase();
  const mediaUrl = artworkMedia?.url.toLowerCase();
  const isGlbMedia = mediaMimeType === "model/gltf-binary" || mediaMimeType === "model/gltf+json" || (mediaUrl?.endsWith(".glb") ?? false) || (mediaUrl?.endsWith(".gltf") ?? false);
  return isGlbMedia ? null : nextDrop;
}
function MemesQuickVotePreviewStack(t0) {
  const $ = _c(28);
  const {
    activeDrop,
    isBusy,
    isMobile,
    leftThisRoundCount,
    nextDrop,
    onAdvanceStart,
    onSkip,
    onVoteWithSwipe,
    sessionId,
    swipeVoteAmount,
    uncastPower,
    unratedCount,
    votingLabel
  } = t0;
  const preloadedCardRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const preloadedCard = preloadedCardRef.current;
      if (!preloadedCard) {
        return;
      }
      preloadedCard.setAttribute("inert", "");
      return () => {
        preloadedCard.removeAttribute("inert");
      };
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== nextDrop) {
    t2 = [nextDrop];
    $[1] = nextDrop;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== isMobile || $[4] !== leftThisRoundCount || $[5] !== nextDrop || $[6] !== sessionId || $[7] !== swipeVoteAmount || $[8] !== uncastPower || $[9] !== unratedCount || $[10] !== votingLabel) {
    t3 = nextDrop && <div ref={preloadedCardRef} aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-inset-0 tw-overflow-hidden tw-opacity-0" data-testid="quick-vote-preview-card-next"><MemesQuickVotePreview key={`next:${sessionId}:${nextDrop.id}`} drop={nextDrop} isBusy={false} isMobile={isMobile} leftThisRoundCount={leftThisRoundCount} renderMode="preloaded" swipeVoteAmount={swipeVoteAmount} uncastPower={uncastPower} unratedCount={unratedCount} votingLabel={votingLabel} onAdvanceStart={_temp} onSkip={_temp2} onVoteWithSwipe={_temp3} /></div>;
    $[3] = isMobile;
    $[4] = leftThisRoundCount;
    $[5] = nextDrop;
    $[6] = sessionId;
    $[7] = swipeVoteAmount;
    $[8] = uncastPower;
    $[9] = unratedCount;
    $[10] = votingLabel;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const t4 = `active:${sessionId}:${activeDrop.id}`;
  let t5;
  if ($[12] !== activeDrop || $[13] !== isBusy || $[14] !== isMobile || $[15] !== leftThisRoundCount || $[16] !== onAdvanceStart || $[17] !== onSkip || $[18] !== onVoteWithSwipe || $[19] !== swipeVoteAmount || $[20] !== t4 || $[21] !== uncastPower || $[22] !== unratedCount || $[23] !== votingLabel) {
    t5 = <div className="tw-relative tw-z-10 tw-h-full"><MemesQuickVotePreview key={t4} drop={activeDrop} isBusy={isBusy} isMobile={isMobile} leftThisRoundCount={leftThisRoundCount} renderMode="active" swipeVoteAmount={swipeVoteAmount} uncastPower={uncastPower} unratedCount={unratedCount} votingLabel={votingLabel} onAdvanceStart={onAdvanceStart} onSkip={onSkip} onVoteWithSwipe={onVoteWithSwipe} /></div>;
    $[12] = activeDrop;
    $[13] = isBusy;
    $[14] = isMobile;
    $[15] = leftThisRoundCount;
    $[16] = onAdvanceStart;
    $[17] = onSkip;
    $[18] = onVoteWithSwipe;
    $[19] = swipeVoteAmount;
    $[20] = t4;
    $[21] = uncastPower;
    $[22] = unratedCount;
    $[23] = votingLabel;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] !== t3 || $[26] !== t5) {
    t6 = <div className="tw-relative tw-h-full tw-w-full">{t3}{t5}</div>;
    $[25] = t3;
    $[26] = t5;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  return t6;
}
function _temp3() {}
function _temp2() {}
function _temp() {}
function MemesQuickVotePreviewPane(t0) {
  const $ = _c(17);
  const {
    activeDrop,
    className,
    isBusy,
    isMobile,
    leftThisRoundCount,
    nextDrop,
    onAdvanceStart,
    onSkip,
    onVoteWithSwipe,
    sessionId,
    swipeVoteAmount,
    uncastPower,
    unratedCount,
    votingLabel
  } = t0;
  let t1;
  if ($[0] !== activeDrop || $[1] !== isBusy || $[2] !== isMobile || $[3] !== leftThisRoundCount || $[4] !== nextDrop || $[5] !== onAdvanceStart || $[6] !== onSkip || $[7] !== onVoteWithSwipe || $[8] !== sessionId || $[9] !== swipeVoteAmount || $[10] !== uncastPower || $[11] !== unratedCount || $[12] !== votingLabel) {
    t1 = <MemesQuickVotePreviewStack activeDrop={activeDrop} isBusy={isBusy} isMobile={isMobile} leftThisRoundCount={leftThisRoundCount} nextDrop={nextDrop} sessionId={sessionId} swipeVoteAmount={swipeVoteAmount} uncastPower={uncastPower} unratedCount={unratedCount} votingLabel={votingLabel} onAdvanceStart={onAdvanceStart} onSkip={onSkip} onVoteWithSwipe={onVoteWithSwipe} />;
    $[0] = activeDrop;
    $[1] = isBusy;
    $[2] = isMobile;
    $[3] = leftThisRoundCount;
    $[4] = nextDrop;
    $[5] = onAdvanceStart;
    $[6] = onSkip;
    $[7] = onVoteWithSwipe;
    $[8] = sessionId;
    $[9] = swipeVoteAmount;
    $[10] = uncastPower;
    $[11] = unratedCount;
    $[12] = votingLabel;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  let t2;
  if ($[14] !== className || $[15] !== t1) {
    t2 = <div className={className}>{t1}</div>;
    $[14] = className;
    $[15] = t1;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  return t2;
}
function MemesQuickVoteControlsPane(t0) {
  const $ = _c(22);
  const {
    className,
    customValue,
    drop,
    feedbackAmount,
    feedbackSource,
    isCustomOpen,
    isSubmitting,
    isVoteFeedbackActive,
    leftThisRoundCount,
    latestUsedAmount,
    quickAmounts,
    uncastPower,
    unratedCount,
    votingLabel,
    onCustomChange,
    onCustomSubmit,
    onOpenCustom,
    onSkip,
    onVoteAmount
  } = t0;
  let t1;
  if ($[0] !== customValue || $[1] !== drop || $[2] !== feedbackAmount || $[3] !== feedbackSource || $[4] !== isCustomOpen || $[5] !== isSubmitting || $[6] !== isVoteFeedbackActive || $[7] !== latestUsedAmount || $[8] !== leftThisRoundCount || $[9] !== onCustomChange || $[10] !== onCustomSubmit || $[11] !== onOpenCustom || $[12] !== onSkip || $[13] !== onVoteAmount || $[14] !== quickAmounts || $[15] !== uncastPower || $[16] !== unratedCount || $[17] !== votingLabel) {
    t1 = <MemesQuickVoteControls customValue={customValue} drop={drop} isCustomOpen={isCustomOpen} isSubmitting={isSubmitting} feedbackAmount={feedbackAmount} feedbackSource={feedbackSource} isVoteFeedbackActive={isVoteFeedbackActive} leftThisRoundCount={leftThisRoundCount} latestUsedAmount={latestUsedAmount} quickAmounts={quickAmounts} uncastPower={uncastPower} unratedCount={unratedCount} votingLabel={votingLabel} onCustomChange={onCustomChange} onCustomSubmit={onCustomSubmit} onOpenCustom={onOpenCustom} onSkip={onSkip} onVoteAmount={onVoteAmount} />;
    $[0] = customValue;
    $[1] = drop;
    $[2] = feedbackAmount;
    $[3] = feedbackSource;
    $[4] = isCustomOpen;
    $[5] = isSubmitting;
    $[6] = isVoteFeedbackActive;
    $[7] = latestUsedAmount;
    $[8] = leftThisRoundCount;
    $[9] = onCustomChange;
    $[10] = onCustomSubmit;
    $[11] = onOpenCustom;
    $[12] = onSkip;
    $[13] = onVoteAmount;
    $[14] = quickAmounts;
    $[15] = uncastPower;
    $[16] = unratedCount;
    $[17] = votingLabel;
    $[18] = t1;
  } else {
    t1 = $[18];
  }
  let t2;
  if ($[19] !== className || $[20] !== t1) {
    t2 = <div className={className}>{t1}</div>;
    $[19] = className;
    $[20] = t1;
    $[21] = t2;
  } else {
    t2 = $[21];
  }
  return t2;
}
function MemesQuickVoteDialogContent(t0) {
  const $ = _c(88);
  const {
    activeDrop,
    isMobile,
    leftThisRoundCount,
    latestUsedAmount,
    nextDrop,
    onClose,
    recentAmounts,
    sessionId,
    submitVote,
    skipDrop,
    uncastPower,
    unratedCount,
    votingLabel
  } = t0;
  const maxRating = activeDrop.context_profile_context?.max_rating ?? 0;
  let t1;
  if ($[0] !== maxRating) {
    t1 = maxRating > 0 ? getDefaultQuickVoteAmount(maxRating) : 1;
    $[0] = maxRating;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const defaultAmount = t1;
  let t2;
  if ($[2] !== defaultAmount) {
    t2 = () => `${defaultAmount}`;
    $[2] = defaultAmount;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [customValue, setCustomValue] = useState(t2);
  let t3;
  if ($[4] !== recentAmounts.length) {
    t3 = () => recentAmounts.length === 0;
    $[4] = recentAmounts.length;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [isCustomOpen, setIsCustomOpen] = useState(t3);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [voteFeedback, setVoteFeedback] = useState(null);
  const barVoteTimeoutRef = useRef(null);
  const isVoteFeedbackActive = voteFeedback !== null;
  const isControlsSubmitting = isAdvancing || isVoteFeedbackActive;
  let t4;
  if ($[6] !== latestUsedAmount || $[7] !== maxRating) {
    t4 = latestUsedAmount === null ? null : normalizeQuickVoteAmount(latestUsedAmount, maxRating);
    $[6] = latestUsedAmount;
    $[7] = maxRating;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const normalizedLatestUsedAmount = t4;
  let t5;
  if ($[9] !== customValue || $[10] !== maxRating) {
    t5 = normalizeQuickVoteAmount(customValue, maxRating);
    $[9] = customValue;
    $[10] = maxRating;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const normalizedCustomAmount = t5;
  let t6;
  bb0: {
    if (maxRating <= 0) {
      let t7;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = [];
        $[12] = t7;
      } else {
        t7 = $[12];
      }
      t6 = t7;
      break bb0;
    }
    let t7;
    if ($[13] !== maxRating || $[14] !== recentAmounts) {
      let t8;
      if ($[16] !== maxRating) {
        t8 = amount => normalizeQuickVoteAmount(amount, maxRating);
        $[16] = maxRating;
        $[17] = t8;
      } else {
        t8 = $[17];
      }
      t7 = Array.from(new Set(recentAmounts.map(t8).filter(_temp4))).sort(_temp5);
      $[13] = maxRating;
      $[14] = recentAmounts;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    t6 = t7;
  }
  const visibleQuickAmounts = t6;
  const swipeVoteAmount = isCustomOpen ? normalizedCustomAmount : normalizedLatestUsedAmount ?? normalizedCustomAmount;
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (barVoteTimeoutRef.current === null) {
        return;
      }
      globalThis.clearTimeout(barVoteTimeoutRef.current);
      barVoteTimeoutRef.current = null;
    };
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const clearBarVoteTimeout = t7;
  let t8;
  if ($[19] !== activeDrop || $[20] !== submitVote) {
    t8 = amount_1 => {
      submitVote(activeDrop, amount_1).then(wasQueued => {
        if (!wasQueued) {
          setIsAdvancing(false);
          setVoteFeedback(null);
        }
      }).catch(() => {
        setIsAdvancing(false);
        setVoteFeedback(null);
      });
    };
    $[19] = activeDrop;
    $[20] = submitVote;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const queueVoteAmount = t8;
  let t10;
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => () => {
      clearBarVoteTimeout();
    };
    t10 = [clearBarVoteTimeout];
    $[22] = t10;
    $[23] = t9;
  } else {
    t10 = $[22];
    t9 = $[23];
  }
  useEffect(t9, t10);
  let t11;
  if ($[24] !== isAdvancing || $[25] !== isVoteFeedbackActive || $[26] !== maxRating || $[27] !== queueVoteAmount) {
    t11 = (amount_2, source) => {
      if (isAdvancing || isVoteFeedbackActive) {
        return;
      }
      const normalizedAmount = normalizeQuickVoteAmount(amount_2, maxRating);
      if (normalizedAmount === null) {
        return;
      }
      setIsAdvancing(true);
      setVoteFeedback({
        amount: normalizedAmount,
        source
      });
      clearBarVoteTimeout();
      barVoteTimeoutRef.current = globalThis.setTimeout(() => {
        barVoteTimeoutRef.current = null;
        setIsAdvancing(true);
        queueVoteAmount(normalizedAmount);
      }, QUICK_VOTE_BAR_FEEDBACK_DURATION_MS);
    };
    $[24] = isAdvancing;
    $[25] = isVoteFeedbackActive;
    $[26] = maxRating;
    $[27] = queueVoteAmount;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const handleBarVoteAmount = t11;
  let t12;
  if ($[29] !== activeDrop || $[30] !== skipDrop) {
    t12 = () => {
      skipDrop(activeDrop).then(wasQueued_0 => {
        if (!wasQueued_0) {
          setIsAdvancing(false);
        }
      }).catch(() => {
        setIsAdvancing(false);
      });
    };
    $[29] = activeDrop;
    $[30] = skipDrop;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const queueSkip = t12;
  let t13;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setIsAdvancing(true);
    };
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const handleAdvanceStart = t13;
  let t14;
  if ($[33] !== isControlsSubmitting || $[34] !== queueSkip) {
    t14 = () => {
      if (isControlsSubmitting) {
        return;
      }
      setIsAdvancing(true);
      queueSkip();
    };
    $[33] = isControlsSubmitting;
    $[34] = queueSkip;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const handleSkip = t14;
  let t15;
  if ($[36] !== queueVoteAmount || $[37] !== swipeVoteAmount) {
    t15 = () => {
      if (swipeVoteAmount === null) {
        setIsAdvancing(false);
        return;
      }
      setIsAdvancing(true);
      queueVoteAmount(swipeVoteAmount);
    };
    $[36] = queueVoteAmount;
    $[37] = swipeVoteAmount;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const handleVoteWithSwipe = t15;
  let t16;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = value => {
      if (value === "") {
        setCustomValue("");
        return;
      }
      setCustomValue(value.replace(/[^\d]/g, ""));
    };
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const handleCustomChange = t16;
  let t17;
  if ($[40] !== customValue || $[41] !== handleBarVoteAmount) {
    t17 = () => {
      handleBarVoteAmount(customValue, "custom-submit");
    };
    $[40] = customValue;
    $[41] = handleBarVoteAmount;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  const handleCustomSubmit = t17;
  let t18;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      setIsCustomOpen(_temp6);
    };
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  const handleToggleCustom = t18;
  let t19;
  if ($[44] !== handleBarVoteAmount) {
    t19 = amount_3 => {
      handleBarVoteAmount(amount_3, "quick-amount");
    };
    $[44] = handleBarVoteAmount;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  const handleQuickAmountVote = t19;
  let t20;
  if ($[46] !== activeDrop || $[47] !== handleVoteWithSwipe || $[48] !== isControlsSubmitting || $[49] !== isMobile || $[50] !== leftThisRoundCount || $[51] !== nextDrop || $[52] !== queueSkip || $[53] !== sessionId || $[54] !== swipeVoteAmount || $[55] !== uncastPower || $[56] !== unratedCount || $[57] !== votingLabel) {
    t20 = {
      activeDrop,
      isBusy: isControlsSubmitting,
      isMobile,
      leftThisRoundCount,
      nextDrop,
      onAdvanceStart: handleAdvanceStart,
      onSkip: queueSkip,
      onVoteWithSwipe: handleVoteWithSwipe,
      sessionId,
      swipeVoteAmount,
      uncastPower,
      unratedCount,
      votingLabel
    };
    $[46] = activeDrop;
    $[47] = handleVoteWithSwipe;
    $[48] = isControlsSubmitting;
    $[49] = isMobile;
    $[50] = leftThisRoundCount;
    $[51] = nextDrop;
    $[52] = queueSkip;
    $[53] = sessionId;
    $[54] = swipeVoteAmount;
    $[55] = uncastPower;
    $[56] = unratedCount;
    $[57] = votingLabel;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  const previewPaneProps = t20 satisfies Omit<MemesQuickVotePreviewPaneProps, "className">;
  const t21 = voteFeedback?.amount ?? null;
  const t22 = voteFeedback?.source ?? null;
  let t23;
  if ($[59] !== activeDrop || $[60] !== customValue || $[61] !== handleCustomSubmit || $[62] !== handleQuickAmountVote || $[63] !== handleSkip || $[64] !== isControlsSubmitting || $[65] !== isCustomOpen || $[66] !== isVoteFeedbackActive || $[67] !== leftThisRoundCount || $[68] !== normalizedLatestUsedAmount || $[69] !== t21 || $[70] !== t22 || $[71] !== uncastPower || $[72] !== unratedCount || $[73] !== visibleQuickAmounts || $[74] !== votingLabel) {
    t23 = {
      customValue,
      drop: activeDrop,
      feedbackAmount: t21,
      feedbackSource: t22,
      isCustomOpen,
      isSubmitting: isControlsSubmitting,
      isVoteFeedbackActive,
      leftThisRoundCount,
      latestUsedAmount: normalizedLatestUsedAmount,
      quickAmounts: visibleQuickAmounts,
      uncastPower,
      unratedCount,
      votingLabel,
      onCustomChange: handleCustomChange,
      onCustomSubmit: handleCustomSubmit,
      onOpenCustom: handleToggleCustom,
      onSkip: handleSkip,
      onVoteAmount: handleQuickAmountVote
    };
    $[59] = activeDrop;
    $[60] = customValue;
    $[61] = handleCustomSubmit;
    $[62] = handleQuickAmountVote;
    $[63] = handleSkip;
    $[64] = isControlsSubmitting;
    $[65] = isCustomOpen;
    $[66] = isVoteFeedbackActive;
    $[67] = leftThisRoundCount;
    $[68] = normalizedLatestUsedAmount;
    $[69] = t21;
    $[70] = t22;
    $[71] = uncastPower;
    $[72] = unratedCount;
    $[73] = visibleQuickAmounts;
    $[74] = votingLabel;
    $[75] = t23;
  } else {
    t23 = $[75];
  }
  const controlsPaneProps = t23 satisfies Omit<MemesQuickVoteControlsPaneProps, "className">;
  let t24;
  if ($[76] !== isMobile || $[77] !== leftThisRoundCount || $[78] !== onClose || $[79] !== unratedCount) {
    t24 = isMobile && <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-solid tw-border-white/5 tw-bg-black/40 tw-px-4 tw-pb-3 tw-pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] tw-backdrop-blur-xl md:tw-hidden"><button type="button" onClick={onClose} data-autofocus="true" className="tw-inline-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-white/5 tw-bg-white/[0.05] tw-text-iron-400 tw-shadow-inner tw-transition-colors active:tw-bg-white/10" aria-label="Close quick vote"><XMarkIcon className="tw-size-5 tw-shrink-0" /></button><div className="tw-flex tw-min-w-0 tw-flex-col tw-items-center tw-justify-center tw-px-3"><span className="tw-truncate tw-text-[13px] tw-font-bold tw-leading-tight tw-text-iron-300">{formatMemesQuickVoteLeftThisRoundText(leftThisRoundCount)}</span><span className="tw-truncate tw-text-[12px] tw-font-medium tw-leading-tight tw-text-iron-500">{formatMemesQuickVoteUnratedText(unratedCount)}</span></div><div className="tw-size-10 tw-shrink-0" aria-hidden="true" /></div>;
    $[76] = isMobile;
    $[77] = leftThisRoundCount;
    $[78] = onClose;
    $[79] = unratedCount;
    $[80] = t24;
  } else {
    t24 = $[80];
  }
  let t25;
  if ($[81] !== controlsPaneProps || $[82] !== isMobile || $[83] !== previewPaneProps) {
    t25 = isMobile ? <div className="tw-relative tw-z-10 tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col tw-overflow-hidden"><MemesQuickVotePreviewPane className="tw-min-h-0 tw-flex-1" {...previewPaneProps} /><MemesQuickVoteControlsPane className="tw-shrink-0 tw-border-t tw-border-solid tw-border-white/5 tw-bg-[#0a0a0a] md:tw-hidden" {...controlsPaneProps} /></div> : <><MemesQuickVotePreviewPane className="tw-min-h-0 tw-flex-1 md:tw-min-h-0 md:tw-border-y-0 md:tw-border-b-0 md:tw-border-l-0 md:tw-border-r md:tw-border-solid md:tw-border-white/10" {...previewPaneProps} /><MemesQuickVoteControlsPane className="tw-shrink-0 md:tw-min-h-0 md:tw-self-stretch" {...controlsPaneProps} /></>;
    $[81] = controlsPaneProps;
    $[82] = isMobile;
    $[83] = previewPaneProps;
    $[84] = t25;
  } else {
    t25 = $[84];
  }
  let t26;
  if ($[85] !== t24 || $[86] !== t25) {
    t26 = <div className="tw-flex tw-h-full tw-flex-col md:tw-grid md:tw-min-h-0 md:tw-grid-cols-[minmax(0,1.22fr)_minmax(25rem,1fr)] md:tw-items-stretch">{t24}{t25}</div>;
    $[85] = t24;
    $[86] = t25;
    $[87] = t26;
  } else {
    t26 = $[87];
  }
  return t26;
}
function _temp6(current) {
  return !current;
}
function _temp5(left, right) {
  return left - right;
}
function _temp4(amount_0) {
  return amount_0 !== null;
}
export default function MemesQuickVoteDialog(t0) {
  const $ = _c(48);
  const {
    isOpen,
    sessionId,
    onClose,
    activeDrop,
    hasDiscoveryError,
    isExhausted,
    isRestartingRound,
    leftThisRoundCount,
    latestUsedAmount,
    nextDrop,
    recentAmounts,
    retryDiscovery,
    submitVote,
    skipDrop,
    uncastPower,
    unratedCount,
    votingLabel
  } = t0;
  const dialogRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);
  const previousBodyOverflowRef = useRef("");
  const isMobile = useMediaQuery(QUICK_VOTE_MOBILE_QUERY);
  const showStandaloneStateShellClose = activeDrop === null && !isExhausted;
  let t1;
  let t2;
  if ($[0] !== isOpen) {
    t1 = () => {
      const dialog = dialogRef.current;
      if (!dialog) {
        return;
      }
      if (!isOpen) {
        if (dialog.open) {
          dialog.close();
        }
        document.body.style.overflow = previousBodyOverflowRef.current;
        previouslyFocusedElementRef.current?.focus();
        return;
      }
      previouslyFocusedElementRef.current = document.activeElement as HTMLElement;
      previousBodyOverflowRef.current = document.body.style.overflow;
      if (!dialog.open) {
        dialog.showModal();
      }
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => {
        const autofocusTarget = dialog.querySelector("[data-autofocus='true']");
        autofocusTarget?.focus();
      });
      return () => {
        document.body.style.overflow = previousBodyOverflowRef.current;
      };
    };
    t2 = [isOpen];
    $[0] = isOpen;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== onClose) {
    t3 = () => {
      const dialog_0 = dialogRef.current;
      if (!dialog_0) {
        return;
      }
      const handleCancel = event => {
        event.preventDefault();
        onClose();
      };
      dialog_0.addEventListener("cancel", handleCancel);
      return () => {
        dialog_0.removeEventListener("cancel", handleCancel);
      };
    };
    t4 = [onClose];
    $[3] = onClose;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let dialogBody;
  if (isRestartingRound) {
    let t5;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <MemesQuickVoteDialogRestartState />;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    dialogBody = t5;
  } else {
    if (isExhausted) {
      let t5;
      if ($[7] !== onClose) {
        t5 = <MemesQuickVoteDialogDoneState description="No unrated memes are left in quick vote right now." onClose={onClose} title="You're all caught up" />;
        $[7] = onClose;
        $[8] = t5;
      } else {
        t5 = $[8];
      }
      dialogBody = t5;
    } else {
      if (!activeDrop && hasDiscoveryError) {
        let t5;
        if ($[9] !== retryDiscovery) {
          t5 = <MemesQuickVoteDialogErrorState onRetry={retryDiscovery} />;
          $[9] = retryDiscovery;
          $[10] = t5;
        } else {
          t5 = $[10];
        }
        dialogBody = t5;
      } else {
        if (!activeDrop) {
          let t5;
          if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
            t5 = <MemesQuickVoteDialogSkeleton />;
            $[11] = t5;
          } else {
            t5 = $[11];
          }
          dialogBody = t5;
        } else {
          const contentResetKey = `${sessionId}:${activeDrop.id}:${isOpen ? "open" : "closed"}`;
          let t5;
          if ($[12] !== isOpen || $[13] !== nextDrop) {
            t5 = getQuickVotePreloadedNextDrop(isOpen, nextDrop);
            $[12] = isOpen;
            $[13] = nextDrop;
            $[14] = t5;
          } else {
            t5 = $[14];
          }
          const preloadedNextDrop = t5;
          let t6;
          if ($[15] !== activeDrop || $[16] !== contentResetKey || $[17] !== isMobile || $[18] !== latestUsedAmount || $[19] !== leftThisRoundCount || $[20] !== onClose || $[21] !== preloadedNextDrop || $[22] !== recentAmounts || $[23] !== sessionId || $[24] !== skipDrop || $[25] !== submitVote || $[26] !== uncastPower || $[27] !== unratedCount || $[28] !== votingLabel) {
            t6 = <MemesQuickVoteDialogContent key={contentResetKey} activeDrop={activeDrop} isMobile={isMobile} leftThisRoundCount={leftThisRoundCount} latestUsedAmount={latestUsedAmount} nextDrop={preloadedNextDrop} onClose={onClose} recentAmounts={recentAmounts} sessionId={sessionId} submitVote={submitVote} skipDrop={skipDrop} uncastPower={uncastPower} unratedCount={unratedCount} votingLabel={votingLabel} />;
            $[15] = activeDrop;
            $[16] = contentResetKey;
            $[17] = isMobile;
            $[18] = latestUsedAmount;
            $[19] = leftThisRoundCount;
            $[20] = onClose;
            $[21] = preloadedNextDrop;
            $[22] = recentAmounts;
            $[23] = sessionId;
            $[24] = skipDrop;
            $[25] = submitVote;
            $[26] = uncastPower;
            $[27] = unratedCount;
            $[28] = votingLabel;
            $[29] = t6;
          } else {
            t6 = $[29];
          }
          dialogBody = t6;
        }
      }
    }
  }
  let t5;
  if ($[30] !== onClose) {
    t5 = event_0 => {
      if (event_0.target !== event_0.currentTarget) {
        return;
      }
      onClose();
    };
    $[30] = onClose;
    $[31] = t5;
  } else {
    t5 = $[31];
  }
  const t6 = !isMobile || showStandaloneStateShellClose ? "true" : undefined;
  const t7 = `tw-absolute tw-right-4 tw-top-[calc(env(safe-area-inset-top,0px)+0.75rem)] tw-z-20 tw-size-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-white/5 tw-bg-white/[0.05] tw-text-iron-400 tw-shadow-inner tw-backdrop-blur-md tw-transition-colors active:tw-bg-white/10 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 desktop-hover:hover:tw-text-white md:tw-right-6 md:tw-top-6 ${showStandaloneStateShellClose ? "tw-inline-flex md:tw-inline-flex" : "tw-hidden md:tw-inline-flex"}`;
  let t8;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <XMarkIcon className="tw-size-5 tw-shrink-0" />;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== onClose || $[34] !== t6 || $[35] !== t7) {
    t9 = <button type="button" data-autofocus={t6} onClick={onClose} className={t7} aria-label="Close quick vote">{t8}</button>;
    $[33] = onClose;
    $[34] = t6;
    $[35] = t7;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== dialogBody) {
    t10 = <div className="tw-relative tw-z-10 tw-min-h-0 tw-flex-1 tw-overflow-hidden md:tw-p-0">{dialogBody}</div>;
    $[37] = dialogBody;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== t10 || $[40] !== t9) {
    t11 = <div className="tw-relative tw-flex tw-h-full tw-max-h-full tw-w-full tw-flex-col tw-overflow-hidden tw-bg-[#0a0a0a] tw-shadow-[0_0_80px_rgba(0,0,0,0.8)] md:tw-h-[38rem] md:tw-max-h-[min(calc(100vh-3rem),38rem)] md:tw-max-w-[68rem] md:tw-rounded-2xl md:tw-border md:tw-border-solid md:tw-border-white/10">{t9}{t10}</div>;
    $[39] = t10;
    $[40] = t9;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== t11 || $[43] !== t5) {
    t12 = <div className="tw-flex tw-h-full tw-w-full tw-items-stretch tw-justify-center tw-bg-gray-600 tw-bg-opacity-50 tw-backdrop-blur-[1px] md:tw-items-center md:tw-p-6" onClick={t5}>{t11}</div>;
    $[42] = t11;
    $[43] = t5;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== sessionId || $[46] !== t12) {
    t13 = <dialog ref={dialogRef} data-session-id={sessionId} className="tailwind-scope tw-fixed tw-inset-0 tw-m-0 tw-h-[100dvh] tw-max-h-[100dvh] tw-w-screen tw-max-w-none tw-overflow-hidden tw-border-none tw-bg-transparent tw-p-0" aria-label="Memes quick vote">{t12}</dialog>;
    $[45] = sessionId;
    $[46] = t12;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  return t13;
}